// src/components/dashboard/FiltersBar/PlatformFilter.jsx
import React from 'react';
import { TextField, MenuItem, Box, alpha, useTheme, Typography, Chip } from '@mui/material';
import { Devices, Computer, PhoneAndroid } from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { setPlatform } from '../../../store/slices/dashboardSlice';

// Common platform options based on typical betting platforms
const PLATFORM_OPTIONS = [
  { value: '', label: 'All Platforms', icon: Devices },
  { value: 'web', label: 'Web Platform', icon: Computer },
  { value: 'mobile', label: 'Mobile App', icon: PhoneAndroid },
  { value: 'desktop', label: 'Desktop App', icon: Computer },
  { value: 'tablet', label: 'Tablet', icon: PhoneAndroid },
];

const PlatformFilter = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { platform } = useSelector((state) => state.dashboard.filters);

  const handleChange = (event) => {
    dispatch(setPlatform(event.target.value));
  };

  const selectedPlatform = PLATFORM_OPTIONS.find(p => p.value === platform);
  const IconComponent = selectedPlatform?.icon || Devices;

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Devices sx={{ color: 'primary.main', fontSize: '1rem' }} />
        <Typography 
          variant="subtitle2" 
          sx={{ 
            fontWeight: 600,
            color: 'text.primary',
            fontSize: '0.875rem'
          }}
        >
          Platform
        </Typography>
        {platform && (
          <Chip
            size="small"
            label={selectedPlatform?.label || platform}
            color="secondary"
            variant="outlined"
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
        )}
      </Box>

      <TextField
        select
        value={platform}
        onChange={handleChange}
        size="medium"
        fullWidth
        sx={{ 
          minWidth: { xs: '100%', sm: 180 },
          '& .MuiOutlinedInput-root': {
            borderRadius: 3,
            background: alpha(theme.palette.background.paper, 0.7),
            backdropFilter: 'blur(10px)',
            transition: 'all 0.3s ease',
            '& fieldset': {
              borderColor: alpha(theme.palette.primary.main, 0.2),
              transition: 'all 0.3s ease',
            },
            '&:hover fieldset': {
              borderColor: alpha(theme.palette.primary.main, 0.4),
              boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.1)}`,
            },
            '&.Mui-focused fieldset': {
              borderColor: theme.palette.primary.main,
              boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.2)}`,
            },
          },
          '& .MuiSelect-select': {
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }
        }}
        SelectProps={{
          displayEmpty: true,
          renderValue: (selected) => {
            const option = PLATFORM_OPTIONS.find(p => p.value === selected);
            const Icon = option?.icon || Devices;
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Icon sx={{ 
                  fontSize: '1rem', 
                  color: selected ? 'primary.main' : 'text.secondary' 
                }} />
                {option?.label || 'All Platforms'}
              </Box>
            );
          },
          MenuProps: {
            PaperProps: {
              sx: {
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                backdropFilter: 'blur(10px)',
                '& .MuiMenuItem-root': {
                  borderRadius: 1,
                  mx: 1,
                  my: 0.5,
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  '&:hover': {
                    background: alpha(theme.palette.primary.main, 0.1),
                  },
                  '&.Mui-selected': {
                    background: alpha(theme.palette.primary.main, 0.2),
                    fontWeight: 600,
                    '&:hover': {
                      background: alpha(theme.palette.primary.main, 0.3),
                    },
                  },
                },
              },
            },
          },
        }}
      >
        {PLATFORM_OPTIONS.map((option) => {
          const Icon = option.icon;
          return (
            <MenuItem key={option.value} value={option.value}>
              <Icon sx={{ 
                fontSize: '1rem', 
                color: option.value === '' ? 'text.secondary' : 'primary.main' 
              }} />
              <Typography variant="body2" fontWeight={600}>
                {option.label}
              </Typography>
            </MenuItem>
          );
        })}
      </TextField>
    </Box>
  );
};

export default PlatformFilter;